'use client';

import { getISODate } from '@hc-frontend/core-utils-date';
import { isDefined } from '@hc-frontend/core-utils-validations';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { IMask, IMaskInput } from 'react-imask';

import { FormGroup } from '../../templates';
import { Tooltip } from '../tooltip/tooltip';
import type { InputProps } from './date-input.types';

function getContainerStyle(withButton: boolean) {
  if (withButton)
    return 'hc-input__container--with-button hc-sidebar-layout--reverse';

  return '';
}

export function DateInput({
  autoFocus = false,
  button,
  className,
  disabled,
  error,
  errorText,
  helpText,
  icon,
  id,
  initialValue,
  label,
  name,
  onChange,
  required,
  tooltip,
  tooltipText,
  withButton = false,
}: InputProps) {
  const showErrors = error;
  const inputId = id || name;

  const ref = useRef(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState<Date | undefined>(initialValue);

  useEffect(() => {
    if (inputRef.current && autoFocus) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const onCompleteHandler = (
    newValue: Date,
    imask: unknown,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const shouldTriggerOnChange =
      onChange &&
      isDefined(newValue) &&
      getISODate(value) !== getISODate(newValue) &&
      event;

    if (shouldTriggerOnChange) {
      onChange(newValue, imask, event);
    }

    setValue(newValue);
  };

  useEffect(() => {
    if (initialValue) {
      setValue(initialValue);
    }
  }, [initialValue]);

  return (
    <FormGroup className={className} disabled={disabled} error={showErrors}>
      <div className="flex flex-row justify-between">
        <label className="hc-label" htmlFor={inputId}>
          {label}
        </label>
        {tooltipText && (
          <Tooltip
            title={tooltipText}
            className="left-[-270px] top-[90px] w-[290px] bg-primary-background-50 text-text-950 min-[535px]:left-[-280px] min-[535px]:top-[95px] tablet:left-[25px] tablet:top-[-5px] tablet:w-[clamp(50px,25vw,307px)]"
          >
            {tooltip}
          </Tooltip>
        )}
      </div>
      <div className={getContainerStyle(withButton)}>
        <div className="hc-form-control--icon-right relative">
          <IMaskInput
            ref={ref}
            inputRef={inputRef}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            /* @ts-expect-error */
            value={value}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            /* @ts-expect-error */
            onComplete={onCompleteHandler}
            onAccept={() => {
              inputRef.current?.classList.toggle(
                'text-gray-200',
                inputRef.current.value === 'MM/DD/YYYY',
              );
            }}
            className={!initialValue ? 'text-gray-200' : ''}
            mask={Date}
            unmask="typed"
            lazy={false}
            pattern="m/`d/`Y"
            placeholder="mm/dd/yyyy"
            blocks={{
              d: {
                placeholderChar: 'D',
                mask: IMask.MaskedRange,
                from: 1,
                to: 31,
                maxLength: 2,
              },
              m: {
                placeholderChar: 'M',
                mask: IMask.MaskedRange,
                from: 1,
                to: 12,
                maxLength: 2,
              },
              Y: {
                placeholderChar: 'Y',
                mask: IMask.MaskedRange,
                from: 1900,
                to: 9999,
                maxLength: 4,
              },
            }}
            format={(date: Date) => {
              if (!date) return '';
              const year = date.getFullYear().toString();
              const month = (date.getMonth() + 1).toString().padStart(2, '0');
              const day = date.getDate().toString().padStart(2, '0');

              return `${month}/${day}/${year}`;
            }}
            parse={(str) => {
              const [month, day, year] = str.split('/');
              return new Date(Number(year), Number(month) - 1, Number(day));
            }}
            required={required}
            disabled={disabled}
            name={name}
            id={inputId}
            aria-invalid={showErrors}
            type="tel"
          />
          {icon}
        </div>
        {button}
      </div>
      <div className="hc-help-text">{helpText}</div>
      <div className="hc-error-text">{errorText}</div>
    </FormGroup>
  );
}
